import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { conditionalRender } from '@repo/toolkit/conditional-render'
import Button from '@repo/ui/button'
import DateStringInput from '@repo/ui/date-string-input'
import { ResetTwoIcon } from '@repo/ui/icons'
import MultiSelectInput from '@repo/ui/multiselect-input'
import SelectInput from '@repo/ui/select-input'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'

import { getCountries } from '../../../../api/countries/get-countries'
import { getFormulas } from '../../../../api/formula/get-formulas'
import { getOptions } from '../../../../api/options/get-options'
import { getPeriods } from '../../../../api/periods/get-periods'
import { getTripPlaces } from '../../../../api/trip-places/get-trip-places'
import {
  AccomodationCategoryEnum,
  accomodationCategoryEnumMapping
} from '../../../../lib/types/enums/product/accomodation-category.enum'
import {
  AcademicZoneEnum,
  academicZoneEnumMapping
} from '../../../../lib/types/enums/staff/academic-zone.enum'
import { JourneyFilters } from '../../../../lib/types/models/product/journey'
import { getSelectedEnumOption } from '../../../../lib/utils/functions/misc.functions'
import { getTranslatedName } from '../../../../lib/utils/functions/translation.functions'
import { childAges } from '../../../../views/pcr-personal-space/utils'

type Props = {
  filter?: JourneyFilters
  onResetFilter: () => void
  onSelectedFilterChange: (filter: JourneyFilters) => void
}

const JourneyListSelectionFilter = ({ filter, onResetFilter, onSelectedFilterChange }: Props) => {
  const { data: periods } = useQuery({
    queryKey: ['periods'],
    queryFn: () => getPeriods({ version: 'BASIC' })
  })

  const { data: countries } = useQuery({
    queryKey: ['countries'],
    queryFn: () => getCountries()
  })

  const { data: formulas } = useQuery({
    queryKey: ['formulas'],
    queryFn: () => getFormulas()
  })

  const { data: tripPlaces } = useQuery({
    queryKey: ['tripPlaces'],
    queryFn: () => getTripPlaces()
  })

  const { data: options } = useQuery({
    queryKey: ['options', { version: 'LIGHT' }],
    queryFn: () => getOptions({ version: 'LIGHT' })
  })

  return (
    <>
      <div className="flex items-center justify-between">
        <Typography variant="tag" color={tailwindColors.grey.DEFAULT}>
          Filtrer :
        </Typography>

        <Button
          variant="destructive"
          size="small"
          icon={ResetTwoIcon}
          label="Réinitialiser"
          type="button"
          onClick={() => onResetFilter()}
        />
      </div>
      <div className="h-3" />
      <div className="flex flex-col gap-3">
        <div className="grid grid-cols-5 gap-3">
          <SelectInput<number>
            label={'Âge'}
            options={childAges.map((age) => ({ label: age.label, value: age.value }))}
            selectedOption={conditionalRender(!!filter?.age, {
              label: childAges.find((age) => age.value === filter!.age)?.label ?? '',
              value: filter!.age!
            })}
            onChange={(age) => onSelectedFilterChange({ ...filter, age })}
          />

          <MultiSelectInput
            searchable
            label={'Pays'}
            variant={'primary'}
            options={countries?.data.map((country) => ({
              label: country.name,
              value: country.name
            }))}
            selectedOptions={filter?.countries?.map((country) => ({
              label: countries?.data?.find((c) => c.name === country)?.name ?? '',
              value: country
            }))}
            onChange={(countries) => onSelectedFilterChange({ ...filter, countries })}
          />
          <MultiSelectInput
            label={'Formule(s)'}
            variant={'primary'}
            searchable
            options={formulas?.data?.map((formula) => ({
              label: getTranslatedName(formula),
              value: getTranslatedName(formula)
            }))}
            selectedOptions={filter?.formulas?.map((formula) => ({
              label: getTranslatedName(
                formulas?.data?.find((f) => getTranslatedName(f) === formula)
              ),
              value: formula
            }))}
            onChange={(formulas) => onSelectedFilterChange({ ...filter, formulas })}
          />

          <MultiSelectInput
            label={'Thème(s) / Option(s)'}
            searchable
            variant={'primary'}
            options={
              options?.data.map((option) => ({
                label: getTranslatedName(option),
                value: getTranslatedName(option)
              })) ?? []
            }
            selectedOptions={filter?.options?.map((item) => ({
              label: getTranslatedName(
                options?.data.find((option) => getTranslatedName(option) === item)
              ),
              value: item
            }))}
            onChange={(options) => onSelectedFilterChange({ ...filter, options })}
          />

          <MultiSelectInput
            label={"Type(s) d'hébergement"}
            variant={'primary'}
            options={Object.values(AccomodationCategoryEnum).map((accomodationType) => ({
              label: accomodationCategoryEnumMapping[accomodationType],
              value: accomodationType
            }))}
            selectedOptions={filter?.accomodationCategories?.map((item) => ({
              label: accomodationCategoryEnumMapping[item],
              value: item
            }))}
            onChange={(accomodationCategories) => {
              onSelectedFilterChange({ ...filter, accomodationCategories })
            }}
          />
        </div>

        <div className="grid grid-cols-6 gap-3 items-center ">
          <MultiSelectInput
            label={'Ville(s) de départ'}
            variant={'primary'}
            searchable
            options={
              tripPlaces?.data.map((tripPlace) => ({
                label: getTranslatedName(tripPlace),
                value: tripPlace.id
              })) ?? []
            }
            selectedOptions={filter?.tripPlaceIds?.map((tripPlaceId) => ({
              label: getTranslatedName(
                tripPlaces?.data.find((tripPlace) => tripPlace.id === tripPlaceId)
              ),
              value: tripPlaceId
            }))}
            onChange={(tripPlaceIds) => onSelectedFilterChange({ ...filter, tripPlaceIds })}
          />
          <MultiSelectInput
            type="number"
            label="Durée(s) en jours"
            variant="grey"
            onChange={(value) => {
              onSelectedFilterChange({
                ...filter,
                durationInDays: value.map((val) => Number(val))
              })
            }}
            selectedOptions={
              filter?.durationInDays?.map((duration) => ({
                label: duration.toString(),
                value: duration
              })) ?? []
            }
          />

          <MultiSelectInput
            searchable
            label={'Période(s)'}
            variant={'warning'}
            options={
              periods?.data.map((period) => ({ label: period.name, value: period.name })) ?? []
            }
            selectedOptions={filter?.periods?.map((period) => ({
              label: periods?.data.find((p) => p.name === period)?.name ?? '',
              value: period
            }))}
            onChange={(periods) => onSelectedFilterChange({ ...filter, periods })}
          />

          <DateStringInput
            mode={'over'}
            disabledPastDates
            label={'Date de début'}
            value={filter?.startDate}
            onChange={(date) => onSelectedFilterChange({ ...filter, startDate: date })}
          />

          <DateStringInput
            mode={'over'}
            disabledPastDates
            label={'Date de fin'}
            value={filter?.endDate}
            onChange={(date) => onSelectedFilterChange({ ...filter, endDate: date })}
          />

          <SelectInput<AcademicZoneEnum>
            label={'Zone académique'}
            options={Object.values(AcademicZoneEnum).map((zone) => ({
              label: academicZoneEnumMapping[zone],
              value: zone
            }))}
            selectedOption={conditionalRender(
              !!filter?.academicZone,
              getSelectedEnumOption(filter?.academicZone, academicZoneEnumMapping)
            )}
            onChange={(academicZone) => onSelectedFilterChange({ ...filter, academicZone })}
          />
        </div>
      </div>
    </>
  )
}

export default JourneyListSelectionFilter
